import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
} from '../../constants/partnerNetwork';
import { PartnerCommission, listMyCommissions } from '../../services/partnerPortal';
import { formatDate, formatMoney, formatRate } from '../../utils/partnerFormat';
import {
  Alert,
  EmptyState,
  Spinner,
  StatCard,
  StatusBadge,
} from '../partner-network/ui';

type Totals = Awaited<ReturnType<typeof listMyCommissions>>['totals'];

const PartnerCommissions: React.FC = () => {
  const [commissions, setCommissions] = useState<PartnerCommission[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    listMyCommissions()
      .then((response) => {
        if (cancelled) return;
        setCommissions(response.data);
        setTotals(response.totals);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load your earnings');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Earnings
            </h1>
            <p className="text-sm text-muted">
              What each referral has earned you, and where the payment has got to.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        {totals && (
          <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label="Pending"
              value={formatMoney(totals.pending, totals.currency)}
              hint="Awaiting approval"
            />
            <StatCard
              label="Due"
              value={formatMoney(totals.due, totals.currency)}
              hint="Approved, being paid"
              tone="warning"
            />
            <StatCard
              label="Paid"
              value={formatMoney(totals.paid, totals.currency)}
              hint="Already in your account"
              tone="success"
            />
            <StatCard
              label="Total earned"
              value={formatMoney(totals.totalEarned, totals.currency)}
              hint="Across every referral"
            />
          </div>
        )}

        <div className="rounded-xl border border-surface-border bg-surface-card">
          {commissions.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No commissions yet"
                description="A commission appears here once one of your referrals closes and the project value is agreed."
                action={
                  <Link to="/partner/referrals" className="btn-secondary py-2.5 px-5">
                    See my referrals
                  </Link>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 font-medium">Referral</th>
                    <th className="px-4 py-3 font-medium">Project value</th>
                    <th className="px-4 py-3 font-medium">Rate</th>
                    <th className="px-4 py-3 font-medium">Commission</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((commission) => (
                    <tr
                      key={commission.id}
                      className="border-b border-surface-border/60 last:border-0"
                    >
                      <td className="px-4 py-3">
                        {commission.referral ? (
                          <Link
                            to={`/partner/referrals/${commission.referral.id}`}
                            className="text-white hover:text-primary-400"
                          >
                            <span className="block font-mono text-xs text-primary-400">
                              {commission.referral.referralId}
                            </span>
                            {commission.referral.businessName}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatMoney(commission.eligibleAmount, commission.currency)}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatRate(commission.commissionRate)}
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {formatMoney(commission.commissionAmount, commission.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={COMMISSION_STATUS_LABELS[commission.status]}
                          tone={COMMISSION_STATUS_TONES[commission.status]}
                        />
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {commission.paidDate ? (
                          <>
                            {formatDate(commission.paidDate)}
                            {commission.paymentReference && (
                              <span className="block max-w-[150px] truncate text-xs text-muted/70">
                                Ref {commission.paymentReference}
                              </span>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnerCommissions;
